import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * Shown when an anonymous viewer tries to act on a shared trip.
 *
 * Copy is benefit-framed ("Save this trip for later"), never product-framed
 * ("Create an account"). Google is the primary button; email is collapsed
 * beneath it.
 *
 * In Phase 1 the API does not enforce this gate, so it appears only when the
 * server reports enforced: true. Until then anonymous Save/Clone just work.
 */
export function AuthGate({
  headline,
  onClose,
  onAuthed,
}: {
  headline: string;
  onClose: () => void;
  onAuthed: () => void;
}) {
  const { refresh } = useAuth();
  const [mode, setMode] = useState<'choose' | 'email'>('choose');
  const [isSignup, setIsSignup] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (isSignup) {
        await api.signup({ email, password, name: name || undefined });
      } else {
        await api.login({ email, password });
      }
      await refresh();
      onAuthed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center
                 bg-ink/40 px-4 pb-0 sm:pb-4"
      role="dialog"
      aria-modal="true"
      aria-label={headline}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-t-2xl sm:rounded-2xl bg-surface p-6 pb-8 sm:pb-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-display text-[1.5rem] leading-tight text-ink">{headline}</h2>
        <p className="mt-2 font-body text-[0.9375rem] text-muted">
          Keep it in your list, and add your own trips when you get back.
        </p>

        {mode === 'choose' ? (
          <div className="mt-5 space-y-2">
            <a href="/api/auth/google/start" className="btn-primary block text-center">
              Continue with Google
            </a>
            <button onClick={() => setMode('email')} className="btn-quiet w-full">
              Use email instead
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            {isSignup && (
              <input
                className="field"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
            )}
            <input
              className="field"
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <input
              className="field"
              type="password"
              required
              minLength={8}
              placeholder="Password (8+ characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isSignup ? 'new-password' : 'current-password'}
            />
            {error && <p className="font-util text-meta text-[#B4462F]">{error}</p>}
            <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60">
              {busy ? 'One moment…' : isSignup ? 'Create account and save' : 'Sign in and save'}
            </button>
            <button
              type="button"
              onClick={() => setIsSignup((v) => !v)}
              className="btn-quiet w-full !text-meta"
            >
              {isSignup ? 'I already have an account' : 'I need an account'}
            </button>
          </form>
        )}

        <button onClick={onClose} className="btn-quiet mt-2 w-full !text-meta text-muted">
          Not now
        </button>
      </div>
    </div>
  );
}
